import React, { useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export const Doctors = () => {
  const [doctors, setDoctors] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingDoctorId, setDeletingDoctorId] = useState(null);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [formData, setFormData] = useState({ full_name: '', phone: '', license_number: '' });

  useEffect(() => {
    fetchDoctors();
  }, []);

  const fetchDoctors = async () => {
    try {
      const response = await api.get('/doctors');
      setDoctors(response.data);
    } catch (error) {
      toast.error('Failed to load doctors');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDoctor) {
        await api.put(`/doctors/${editingDoctor.id}`, formData);
        toast.success('Doctor updated successfully');
      } else {
        await api.post('/doctors', formData);
        toast.success('Doctor added successfully');
      }
      setIsDialogOpen(false);
      setFormData({ full_name: '', phone: '', license_number: '' });
      setEditingDoctor(null);
      fetchDoctors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (doctor) => {
    setEditingDoctor(doctor);
    setFormData({
      full_name: doctor.full_name,
      phone: doctor.phone,
      license_number: doctor.license_number,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id) => {
    setDeletingDoctorId(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/doctors/${deletingDoctorId}`);
      toast.success('Doctor deleted successfully');
      fetchDoctors();
    } catch (error) {
      toast.error('Failed to delete doctor');
    } finally {
      setIsDeleteDialogOpen(false);
      setDeletingDoctorId(null);
    }
  };

  return (
    <div className="p-6 md:p-12" data-testid="doctors-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-sage-800 mb-2">Doctors</h1>
          <p className="text-gray-600">Manage veterinary doctors</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-sage-500 hover:bg-sage-600 text-white rounded-full shadow-lg"
              onClick={() => {
                setEditingDoctor(null);
                setFormData({ full_name: '', phone: '', license_number: '' });
              }}
              data-testid="add-doctor-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Doctor
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle>{editingDoctor ? 'Edit Doctor' : 'Add New Doctor'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="doctor-form">
              <div>
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="rounded-xl mt-2"
                  required
                  data-testid="doctor-name-input"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="rounded-xl mt-2"
                  required
                  data-testid="doctor-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="license_number">License Number</Label>
                <Input
                  id="license_number"
                  value={formData.license_number}
                  onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                  className="rounded-xl mt-2"
                  required
                  data-testid="doctor-license-input"
                />
              </div>
              <Button type="submit" className="w-full rounded-full bg-sage-500 hover:bg-sage-600 text-white" data-testid="doctor-submit-button">
                {editingDoctor ? 'Update Doctor' : 'Add Doctor'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {doctors.length === 0 ? (
          <div className="col-span-3 text-center py-12 text-gray-500">
            No doctors found. Add your first doctor to get started!
          </div>
        ) : (
          doctors.map((doctor, idx) => (
            <div
              key={doctor.id}
              className="bg-white rounded-3xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300"
              data-testid={`doctor-card-${idx}`}
            >
              <div className="w-20 h-20 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">👨‍⚕️</span>
              </div>
              <h3 className="text-xl font-heading font-bold text-sage-800 text-center mb-2">{doctor.full_name}</h3>
              <p className="text-sm text-gray-600 text-center mb-1">📞 {doctor.phone}</p>
              <p className="text-sm text-gray-600 text-center mb-4">🆔 {doctor.license_number}</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(doctor)}
                  className="flex-1 rounded-full"
                  data-testid={`edit-doctor-${idx}`}
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(doctor.id)}
                  className="flex-1 rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`delete-doctor-${idx}`}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the doctor.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-xl">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
